import { FieldError, TextField } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import type { ChangeEventHandler, ComponentProps, FocusEventHandler, HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { v4 as uuid } from "uuid";
import { useFactory } from "../../helpers/reactHooks";
import { Label } from "../label/label";
import styles from "./Input.module.scss";

// FieldsGroup's order type isn't exported from the package root
type FieldGroupOrder = ComponentProps<typeof TextField>["fieldGroupPosition"];

type TextFieldProps = {
  className?: string;
  placeholder?: string;
  value?: Nullable<string>;
  hasError?: boolean;
  fieldGroupPosition?: FieldGroupOrder;
  onChange: ChangeEventHandler<HTMLInputElement>;
  type?: "text" | "number";
  disabled?: boolean;
  id?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  label?: string;
  // accessible name for inputs without a visible label
  "aria-label"?: string;
  min?: number;
  max?: number;
  step?: string;
  error?: string;
  prefix?: ReactNode;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  autoCapitalize?: HTMLAttributes<HTMLInputElement>["autoCapitalize"];
  readOnly?: boolean;
  /** Defaults to "off" */
  autoComplete?: string;
  name?: string;
};

export const Input = forwardRef<HTMLSpanElement, TextFieldProps>(
  (
    {
      className,
      placeholder,
      value,
      error,
      onChange,
      label,
      hasError = false,
      type = "text",
      disabled,
      prefix,
      fieldGroupPosition,
      inputMode,
      autoCapitalize,
      readOnly,
      autoComplete = "off",
      name,
      id,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useFactory(() => uuid());
    const inputId = id ?? generatedId;

    return (
      <span ref={ref} className={classNames(styles.wrapper, className)}>
        {!!label && <Label forId={inputId} labelText={label} />}
        <span ref={ref} className={classNames(styles.textFieldContainer)}>
          {!!prefix && <span className={styles.prefix}>{prefix}</span>}
          <input
            className={classNames(
              styles.textField,
              hasError && styles.error,
              styles[`type-${type}`],
              !!prefix && styles.hasPrefix,
              fieldGroupPosition && styles[fieldGroupPosition],
            )}
            type={type}
            value={value ?? undefined}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete={autoComplete}
            name={name}
            inputMode={inputMode}
            autoCapitalize={autoCapitalize}
            id={inputId}
            aria-invalid={hasError || !!error}
            {...rest}
          />
        </span>
        {!!error && <FieldError className={styles.errorField} errorText={error} />}
      </span>
    );
  },
);
